// Helpers for the value-design spread:
//   - SortableSection: DndContext + SortableContext wrapper for one list
//   - renderFillerSlots: starter chips + empty placeholders trailing a response list
//   - renderLeftEmpties: dashed empties on the left when relievers > pains

import {
  DndContext, closestCenter, type DragEndEvent,
  type SensorDescriptor, type SensorOptions,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { StarterRow, EmptyRow } from './editorialRows'

export type ReorderKind = 'pain' | 'outcome' | 'reliever' | 'creator'

interface SortableSectionProps {
  kind: ReorderKind
  items: string[]
  sensors: SensorDescriptor<SensorOptions>[]
  onReorder: (kind: ReorderKind, activeId: string, overId: string) => void
  children: React.ReactNode
}

export function SortableSection({ kind, items, sensors, onReorder, children }: SortableSectionProps) {
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    onReorder(kind, String(active.id), String(over.id))
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

interface FillerSlotsArgs {
  responseCount: number
  totalSlots: number
  startGridRow: number
  /** Get the visual id label for slot at index */
  responseLabel: (slotIdx: number) => string
  /** Open starter list for uncovered targets — already filtered */
  starters: { targetText: string; targetIdLabel: string; verb: string; onClick: () => void }[]
  keyPrefix: string
}

/** Renders the trailing starter rows + dashed empty placeholders for either
 *  the relievers section or the creators section. */
export function renderFillerSlots({
  responseCount, totalSlots, startGridRow,
  responseLabel, starters, keyPrefix,
}: FillerSlotsArgs) {
  const filler = totalSlots - responseCount
  if (filler <= 0) return null
  return Array.from({ length: filler }).map((_, i) => {
    const slotIdx = responseCount + i
    const gridRow = startGridRow + slotIdx
    if (i < starters.length) {
      const s = starters[i]
      return (
        <StarterRow
          key={`${keyPrefix}-starter-${slotIdx}`}
          side="right"
          gridRow={gridRow}
          id={responseLabel(slotIdx)}
          targetLabel={`${s.targetIdLabel} · ${s.targetText}`}
          verb={s.verb}
          onClick={s.onClick}
        />
      )
    }
    return (
      <EmptyRow
        key={`${keyPrefix}-empty-${slotIdx}`}
        side="right"
        gridRow={gridRow}
        id={responseLabel(slotIdx)}
      />
    )
  })
}

/** Trailing dashed-empty rows for the customer-side section when the OTHER
 *  side has more rows (e.g. more relievers than pains). */
export function renderLeftEmpties({
  itemCount, totalSlots, startGridRow, label, keyPrefix,
}: {
  itemCount: number
  totalSlots: number
  startGridRow: number
  label: (slotIdx: number) => string
  keyPrefix: string
}) {
  const filler = totalSlots - itemCount
  if (filler <= 0) return null
  return Array.from({ length: filler }).map((_, i) => {
    const slotIdx = itemCount + i
    return (
      <EmptyRow
        key={`${keyPrefix}-empty-${slotIdx}`}
        side="left"
        gridRow={startGridRow + slotIdx}
        id={label(slotIdx)}
      />
    )
  })
}
